/**
 * Weather Service using Open-Meteo API
 * 
 * Features:
 * - Free, no API key required
 * - 7-day forecast
 * - Graceful failure (returns null on error, never throws)
 * - Caches results to avoid repeated calls
 */

// Weather code to icon/description mapping
// Based on WMO Weather interpretation codes
const WEATHER_CODES = {
  0: { icon: '☀️', description: 'Clear' },
  1: { icon: '🌤️', description: 'Mostly Clear' },
  2: { icon: '⛅', description: 'Partly Cloudy' },
  3: { icon: '☁️', description: 'Cloudy' },
  45: { icon: '🌫️', description: 'Foggy' },
  48: { icon: '🌫️', description: 'Fog' },
  51: { icon: '🌧️', description: 'Light Drizzle' },
  53: { icon: '🌧️', description: 'Drizzle' },
  55: { icon: '🌧️', description: 'Heavy Drizzle' },
  56: { icon: '🌧️', description: 'Freezing Drizzle' },
  57: { icon: '🌧️', description: 'Freezing Drizzle' },
  61: { icon: '🌧️', description: 'Light Rain' },
  63: { icon: '🌧️', description: 'Rain' },
  65: { icon: '🌧️', description: 'Heavy Rain' },
  66: { icon: '🌧️', description: 'Freezing Rain' },
  67: { icon: '🌧️', description: 'Freezing Rain' },
  71: { icon: '🌨️', description: 'Light Snow' },
  73: { icon: '🌨️', description: 'Snow' },
  75: { icon: '🌨️', description: 'Heavy Snow' },
  77: { icon: '🌨️', description: 'Snow Grains' },
  80: { icon: '🌦️', description: 'Light Showers' },
  81: { icon: '🌦️', description: 'Showers' },
  82: { icon: '⛈️', description: 'Heavy Showers' },
  85: { icon: '🌨️', description: 'Snow Showers' },
  86: { icon: '🌨️', description: 'Heavy Snow Showers' },
  95: { icon: '⛈️', description: 'Thunderstorm' },
  96: { icon: '⛈️', description: 'Thunderstorm w/ Hail' },
  99: { icon: '⛈️', description: 'Thunderstorm w/ Hail' },
};

// Simple in-memory cache
const cache = {
  data: null,
  timestamp: null,
  key: null,
  TTL: 30 * 60 * 1000, // 30 minutes
};

/**
 * Get weather icon and description from WMO code
 */
const getWeatherInfo = (code) => {
  return WEATHER_CODES[code] || { icon: '🌡️', description: 'Unknown' };
};

/**
 * Check if we should show rain warning (useful for pressure washing!)
 */
const isRainyWeather = (code) => {
  const rainyCodes = [51, 53, 55, 56, 57, 61, 63, 65, 66, 67, 80, 81, 82, 95, 96, 99];
  return rainyCodes.includes(code);
};

/**
 * Fetch 7-day weather forecast
 * 
 * @param {number} latitude 
 * @param {number} longitude 
 * @returns {Promise<Object|null>} Weather data or null on failure
 */
export const fetchWeatherForecast = async (latitude, longitude) => {
  // Check cache first
  const cacheKey = `${latitude.toFixed(2)},${longitude.toFixed(2)}`;
  if (
    cache.data && 
    cache.key === cacheKey && 
    cache.timestamp && 
    Date.now() - cache.timestamp < cache.TTL
  ) {
    return cache.data;
  }

  try {
    const url = new URL('https://api.open-meteo.com/v1/forecast');
    url.searchParams.append('latitude', latitude);
    url.searchParams.append('longitude', longitude);
    url.searchParams.append('daily', 'weather_code,temperature_2m_max,temperature_2m_min,precipitation_probability_max');
    url.searchParams.append('temperature_unit', 'fahrenheit');
    url.searchParams.append('timezone', 'auto');
    url.searchParams.append('forecast_days', '14'); // Get 2 weeks for flexibility

    const response = await fetch(url.toString(), {
      signal: AbortSignal.timeout(5000), // 5 second timeout
    });

    if (!response.ok) {
      console.warn('Weather API returned non-OK status:', response.status);
      return null;
    }

    const data = await response.json();

    if (!data.daily || !data.daily.time) {
      console.warn('Weather API returned unexpected format');
      return null;
    }

    // Transform into a more usable format
    const forecast = {};
    data.daily.time.forEach((date, index) => {
      const weatherCode = data.daily.weather_code[index];
      const weatherInfo = getWeatherInfo(weatherCode);
      
      forecast[date] = {
        date,
        high: Math.round(data.daily.temperature_2m_max[index]),
        low: Math.round(data.daily.temperature_2m_min[index]),
        precipChance: data.daily.precipitation_probability_max[index] || 0,
        code: weatherCode,
        icon: weatherInfo.icon,
        description: weatherInfo.description,
        isRainy: isRainyWeather(weatherCode),
      };
    });

    // Cache the result
    cache.data = forecast;
    cache.key = cacheKey;
    cache.timestamp = Date.now();

    return forecast;
  } catch (error) {
    // Silent failure - just log and return null
    console.warn('Weather fetch failed (this is ok):', error.message);
    return null;
  }
};

/**
 * Get weather for a specific date from cached forecast
 * 
 * @param {Object} forecast - The forecast object from fetchWeatherForecast
 * @param {string} dateStr - Date string in YYYY-MM-DD format
 * @returns {Object|null} Weather for that day or null
 */
export const getWeatherForDate = (forecast, dateStr) => {
  if (!forecast || !dateStr) return null;
  return forecast[dateStr] || null;
};

/**
 * Hook-friendly wrapper that handles the full flow
 * Returns weather data keyed by date string
 */
export const weatherService = {
  fetchForecast: fetchWeatherForecast,
  getForDate: getWeatherForDate,
  isRainy: isRainyWeather,
  
  // Clear cache (useful for testing or forcing refresh)
  clearCache: () => {
    cache.data = null;
    cache.timestamp = null;
    cache.key = null;
  },
};

export default weatherService;